import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../ui/Modal'; // Assuming Modal.tsx is in ../ui/
import PaymentMethodsList from './PaymentMethodsList'; // Assuming PaymentMethodsList.tsx is in the same directory
import { Button } from '../ui/Button'; // Assuming Button.tsx is in ../ui/
import {
  listPaymentMethods,
  setDefaultPaymentMethod,
  PaymentMethod,
  Subscription,
  updateSubscriptionAutoRenewal, // Import Subscription type
} from '../../services/subscriptionService';
import { Loader2, AlertTriangle } from 'lucide-react';
import { isAxiosError } from 'axios';

interface NoDefaultCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: Subscription | null; // Pass the active subscription
  onSuccessfullyEnabledRenewal: () => void; // Callback for success
}

export const NoDefaultCardModal: React.FC<NoDefaultCardModalProps> = ({
  isOpen,
  onClose,
  subscription,
  onSuccessfullyEnabledRenewal,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoadingMethods, setIsLoadingMethods] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch payment methods when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoadingMethods(true);
      setError(null);
      listPaymentMethods()
        .then((methods) => {
          if(methods && methods.length !== 0) {
            setPaymentMethods(methods);
            const defaultMethod = methods.find(m => m.isDefault);
            if (defaultMethod) {
              setSelectedPaymentMethodId(defaultMethod.id);
            } else if (methods.length > 0) {
              setSelectedPaymentMethodId(methods[0].id);
            }
          }

        })
        .catch(() => {
          setError(t('failedToLoadChannels'));
        })
        .finally(() => {
          setIsLoadingMethods(false);
        });
    }
  }, [isOpen]);

  const { mutate: toggleAutoRenewal, isPending: isTogglingAutoRenewal } = useMutation({
    mutationFn: updateSubscriptionAutoRenewal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSubscriptions'] });
      setIsProcessing(false);
      onSuccessfullyEnabledRenewal(); // Call the success callback from parent
      onClose();
    },
    onError: (renewalError: Error) => {
      if(isAxiosError(renewalError)) {
        setIsProcessing(false);
        const renewalErrorMessage = renewalError?.response?.data?.message || renewalError.message || t('failedToEnableAutoRenewal');
        setError(renewalErrorMessage);
      } else {
        setIsProcessing(false);
        setError(t('failedToEnableAutoRenewal'));
      }
    }
  })

  const handleSubmit = async () => {
    if (!selectedPaymentMethodId || !subscription) {
      setError(t('pleaseSelectPaymentMethod'));
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Step 1: Set the selected card as default
      await setDefaultPaymentMethod(selectedPaymentMethodId);
      // Invalidate payment methods to refetch with new default
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
      // Also, update the local list to reflect the change immediately for the PaymentMethodsList component
      setPaymentMethods(prevMethods =>
        prevMethods.map(m => ({ ...m, isDefault: m.id === selectedPaymentMethodId }))
      );


      // Step 2: Try to enable auto-renewal again
      toggleAutoRenewal({ subscriptionId: subscription.id, autoRenewal: true });
    } catch (defaultCardError: unknown) {
      if(isAxiosError(defaultCardError)) {
        setIsProcessing(false);
        const defaultCardErrorMessage = defaultCardError?.response?.data?.message || defaultCardError.message || t('failedToSetDefaultPaymentMethod');
        setError(defaultCardErrorMessage);
      } else {
        setIsProcessing(false);
        setError(t('failedToSetDefaultPaymentMethod'));
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('setDefaultPaymentMethod')}>
      <div className="p-6">
        <p className="text-gray-700 mb-4">
          {t('enableAutoRenewalMessage')}
        </p>

        {isLoadingMethods && (
          <div className="flex items-center justify-center my-4">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
            <span>{t('loading')}</span>
          </div>
        )}

        {!isLoadingMethods && paymentMethods.length === 0 && !error && (
          <p className="text-center text-gray-500 my-4">
            {t('noSavedPaymentMethodsExtended')}
          </p>
        )}

        {!isLoadingMethods && paymentMethods.length > 0 && (
          <div className="mb-4 max-h-60 overflow-y-auto"> {/* Added max-h and overflow for scroll */}
            <PaymentMethodsList
              mode="selection" // Specify selection mode
              hideTitle={true}  // Hide the default title
              paymentMethods={paymentMethods}
              isLoading={isLoadingMethods} // Though loading is handled above, pass for completeness
              selectedPaymentMethodId={selectedPaymentMethodId}
              onSelectPaymentMethod={setSelectedPaymentMethodId}
              // We might need to enhance PaymentMethodsList to better highlight the "isDefault" status
              // or allow disabling selection of already default card if that makes sense.
            />
          </div>
        )}

        {error && (
          <div className="my-3 p-3 bg-red-100 text-red-700 rounded-md flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-3">
          <Button variant="outline" onClick={onClose} disabled={isProcessing || isTogglingAutoRenewal}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedPaymentMethodId || isProcessing || isTogglingAutoRenewal || isLoadingMethods || paymentMethods.length === 0}
          >
            {(isProcessing || isTogglingAutoRenewal) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t('setAsDefaultAndEnableRenewal')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Ensure all imports are correct relative to the new file's location.
// For example, Modal might be '../ui/Modal.tsx', Button from '../ui/Button.tsx'.
// PaymentMethodsList is './PaymentMethodsList.tsx'
// services are from '../../services/subscriptionService'
